import {
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Card,
  CardHeader,
  Button,
  CardContent,
  Autocomplete,
  TextField,
  Stack,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  CircularProgress,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  Fade,
  MenuItem,
  ListItemIcon,
  Typography,
  DialogActions,
  DialogContentText,
  Chip,
  Badge,
  styled,
} from "@mui/material";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Ticket,
  DataOrder,
  TicketStatus,
  customerStatusFilterOptions,
  PaginationResponse,
} from "../../common/constants";
import { CustomerStatusRow } from "../../components/Dashboard/CustomerStatusDisplay";
import axiosConfig from "../../axiosConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getExtension,
  getformatDateFromISOFormat,
} from "../../components/Utils/utils";
import TicketDetail from "../TicketDetail";
import {
  Close,
  InfoOutlined,
  ListAlt,
  MoreVert,
  SaveAlt,
} from "@mui/icons-material";
import DownloadImageDialog from "../../components/Dashboard/DownloadImageDialog";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../components/Dashboard/ConfirmDialog";
import OrderDetails from "../../components/Dashboard/OrderDetails";
import { estimationAmountAsync } from "../../features/estimationAmount/estimationAmountSlice";
import {
  addEstimationToList,
  removeEstimation,
} from "../../features/estimationAmountList/estimationAmountListSlice";
import { useAppDispatch } from "../../app/hooks";
import LoadingDialog from "../../components/LoadingDialog/LoadingDialog";
import { reset, update } from "../../features/customerInfo/customerInfoSlice";
import { setIsCreatedOrder } from "../../features/accountInfo/accountInfoSlice";
import { createComment } from "../../services/commentService";
import { showSnackbar } from "../../features/snackbar/snackbarSlice";
import { changeProcess } from "../../features/managementProcess/managementProcessSlice";
import { getS3ObjectPresignedUrl } from "../../services/s3Service";

type FailedEstimate = {
  id: string;
  file_name: string;
  s3_file_name: string;
  s3_url_image: string;
  order_id: string | null;
  created_at: string;
};

// 自動見積不可ファイル（履歴）の一覧セクション。
// /dashboard/orders とは別に /failed-estimates から取得して表示する。
function FailedEstimateSection(props: { visible: boolean }) {
  const [items, setItems] = useState<FailedEstimate[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const fetchFailed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosConfig.get("/failed-estimates");
      const list: FailedEstimate[] = res.data.body?.data ?? [];
      setItems(list);
      const thumbs: Record<string, string> = {};
      await Promise.all(
        list.map(async (it) => {
          try {
            thumbs[it.id] = await getS3ObjectPresignedUrl(it.s3_url_image);
          } catch {
            // サムネイル取得失敗はプレースホルダ表示にフォールバック
          }
        })
      );
      setThumbnails(thumbs);
    } catch {
      // 取得失敗時は空表示
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (props.visible) fetchFailed();
  }, [props.visible, fetchFailed]);

  // 削除確認ダイアログの対象。null のときは閉じている。
  const [deleteTarget, setDeleteTarget] = useState<FailedEstimate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosConfig.delete(`/failed-estimates/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchFailed();
    } catch {
      // 削除に失敗しても画面は維持
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!props.visible) return null;
  if (!loading && items.length === 0) return null;

  return (
    <Card
      className="mx-auto w-full mt-4"
      sx={{ width: "100%", border: 1, borderColor: "grey.300" }}
    >
      <CardHeader
        sx={{ bgcolor: "#fff8e1", paddingY: "10px" }}
        subheader={"見積不可"}
      />
      <CardContent>
        {items.map((it) => (
          <Stack
            key={it.id}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              borderBottom: "1px solid #eee",
              paddingY: 1,
            }}
          >
            {thumbnails[it.id] ? (
              <img
                src={thumbnails[it.id]}
                alt={it.file_name}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: "cover",
                  border: "1px solid #ddd",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#f5f5f5",
                  border: "1px solid #ddd",
                }}
              />
            )}
            <Chip label="見積不可" color="error" size="small" />
            <Typography sx={{ flexGrow: 1 }}>{it.file_name}</Typography>
            <Button
              href="https://cadtrace.jp/form/"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
            >
              お見積フォームを開く
            </Button>
            <Button
              onClick={() => setDeleteTarget(it)}
              variant="outlined"
              size="small"
              color="inherit"
            >
              削除
            </Button>
          </Stack>
        ))}
      </CardContent>
      {/* 削除は元に戻せないため、実行前に確認する */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => (deleting ? undefined : setDeleteTarget(null))}
      >
        <DialogTitle>見積不可の図面を削除します</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{deleteTarget?.file_name}」を削除しますか？
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            削除すると元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function Row(props: {
  row: DataOrder;
  setTicketId: React.Dispatch<React.SetStateAction<string | undefined>>;
  fetchData: () => {};
  setOrderTicketId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOpenLoadingDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setIsErrorEstOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { row, setTicketId } = props;
  const [expand, setExpand] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket>();
  const [imageData, setImageData] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [isAccept, setIsAccept] = useState<boolean | null>(null);
  const [openAcceptOrRejectDialog, setOpenAcceptOrRejectDialog] =
    useState(false);
  // 差し戻し理由入力モーダル（設計05: 受入拒否は理由必須）
  const [openRejectReasonDialog, setOpenRejectReasonDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const handleDownloadInitialFileClick = async () => {
    setImageData(null);
    setOpenDownloadDialog(true);
    setLoading(true);

    try {
      const response = await axiosConfig({
        method: "get",
        url: `/image/download-init/${selectedTicket?.id}`,
      });

      // Store image data & filename in state
      setImageData(response.data);
      setImageName(selectedTicket!.file_name);
    } catch (error) {
      console.error("Error downloading file", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResultFileClick = async () => {
    setImageData(null);
    setOpenDownloadDialog(true);
    setLoading(true);

    try {
      const res = await axiosConfig({
        method: "get",
        url: `/image/download-res/${selectedTicket?.id}`,
      });

      // Store image data & filename in state
      setImageData(res.data.body.file_content);
      setImageName(res.data.body.file_name);

      if (
        TicketStatus.indexOf("COMPANY_DELIVERED") === selectedTicket?.status
      ) {
        await axiosConfig.patch(`/dashboard/tickets/${selectedTicket?.id}`, {
          status: TicketStatus.indexOf("CUSTOMER_INSPECTION_ONGOING"),
        });
        setIsRefresh(true);
      }
    } catch (error) {
      console.error("Error downloading file", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDownloadDialog(false);
  };

  const handleDownloadFromDialog = () => {
    if (imageData) {
      // Decode base64 string
      const byteCharacters = atob(imageData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Create a Blob object with the correct MIME type
      const blob = new Blob([byteArray], {
        type: `image/${imageName?.split(".").pop() || "png"}`,
      });

      // Create a URL for the Blob object
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", imageName || "file");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleReEstimate = async () => {
    try {
      dispatch(removeEstimation());
      dispatch(setIsCreatedOrder(true));
      dispatch(changeProcess("order"));
      dispatch(reset());
      
      // Get order detail
      const res = await axiosConfig.get(
        `/dashboard/tickets/${row.tickets[0].id}`
      );
      const data = res.data.body;
      const drawingsIdList = row.tickets.map((item: Ticket) => item.drawing_id);
      dispatch(
        update({
          email: data.email ?? "",
          pic: data.pic ?? "",
          picRuby: data.pic_ruby ?? "",
          companyName: data.company_name ?? "",
          companyNameRuby: data.company_name_ruby ?? "",
          postalCode: data.postal_code ?? "",
          address: data.address ?? "",
          address2: data.address2 ?? "",
          phoneNumber: data.phone_number ?? "",
          projectName: data.project_name ?? "",
          remarks: data.remarks ?? "",
          status: data.status ?? "",
          estimateNumber: data.estimation_number ?? "",
          orderId: data.order_id ?? "",
          drawingsId: drawingsIdList.length > 0 ? drawingsIdList : [],
        })
      );
      
      // Estimate ticket
      const listIdTicket = row.tickets.map((item) => {
        return {
          id: item.id,
          fileName: item.file_name,
          s3_url_image: item.s3_url_image,
        };
      });
      
      let invalidEstimateCount = 0;
      let hasError = false;
      
      for (let i = 0; i < listIdTicket.length; i++) {
        const item = listIdTicket[i];
        try {
          const extention = getExtension(item.fileName);
          // Get S3 object presigned URL for displaying image
          const urlImage = await getS3ObjectPresignedUrl(item.s3_url_image);
          
          const resEstimate = await dispatch(
            estimationAmountAsync({
              fileName: item.s3_url_image,
              fileNameOrigin: item.fileName,
              extension: extention,
              urlImage: urlImage,
            })
          );
          
          if (estimationAmountAsync.fulfilled.match(resEstimate)) {
            if (resEstimate.payload.estimationAmount <= 0) {
              invalidEstimateCount++;
            } else {
              dispatch(
                addEstimationToList({
                  ...resEstimate.payload,
                })
              );
            }
          }
        } catch (err) {
          console.error("Error estimating file:", err);
          hasError = true;
          alert("ファイルのアップロードに失敗しました。");
          break;
        }
      }
      
      props.setOpenLoadingDialog(false);
      
      if (!hasError) {
        console.log('Invalid Estimate Count:', invalidEstimateCount);
        if (invalidEstimateCount === 0) {
          navigate("/estimate-delivery-plan");
        } else {
          props.setIsErrorEstOpen(true);
          dispatch(removeEstimation());
        }
      }
    } catch (err) {
      console.error("Error in handleReEstimate:", err);
      props.setOpenLoadingDialog(false);
      alert("処理中にエラーが発生しました。");
    }
  };
  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: "30px" }}>
          {row.tickets.length > 0 && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setExpand(!expand)}
            >
              {expand ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell component="th" scope="row">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {row.order.is_confirmed === false && (
              <Chip label="未発注" color="error" size="small" />
            )}
            {(row.order.unread_comment_count ?? 0) > 0 && (
              <Badge
                color="error"
                badgeContent={row.order.unread_comment_count}
                sx={{ mr: 1 }}
              />
            )}
            <span>{row.order.estimation_number}</span>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            width: "30%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.order.project_name}
        </TableCell>
        <TableCell>
          <div style={{ display: "flex" }}>
            {row.tickets[0].status === 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disableRipple
                disableElevation
                sx={{ marginLeft: "auto" }}
                onClick={async () => {
                  props.setOpenLoadingDialog(true);
                  await handleReEstimate();
                }}
              >
                納期を再設定し見積および発注
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              disableRipple
              disableElevation
              sx={{ marginLeft: row.tickets[0].status === 0 ? 3 : "auto" }}
              onClick={() => {
                props.setOrderTicketId(row.tickets[0].id);
              }}
            >
              詳細
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {row.tickets.length > 0 && (
        <TableRow>
          <TableCell colSpan={12} sx={{ padding: 0 }}>
            <Collapse in={expand} timeout="auto" unmountOnExit>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <TableBody>
                  {row.tickets
                    .filter((ticket) => ticket.id != null)
                    .map((ticketRow: Ticket) => (
                      <TableRow key={ticketRow.id}>
                        <TableCell
                          sx={{
                            width: "5%",
                          }}
                        />
                        <TableCell
                          align="left"
                          sx={{
                            width: "30%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticketRow.file_name}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            width: "15%",
                          }}
                        >
                          {getformatDateFromISOFormat(ticketRow.delivery_at)}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            width: "35%",
                          }}
                        >
                          <CustomerStatusRow status={ticketRow.status} />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            {TicketStatus[ticketRow.status] ===
                              "CUSTOMER_INSPECTION_ONGOING" && (
                              <Button
                                variant="contained"
                                size="small"
                                disableRipple
                                disableElevation
                                onClick={() => {
                                  setSelectedTicket(ticketRow);
                                  setIsAccept(true);
                                  setOpenAcceptOrRejectDialog(true);
                                }}
                              >
                                受入完了
                              </Button>
                            )}
                            {TicketStatus[ticketRow.status] ===
                              "CUSTOMER_INSPECTION_ONGOING" && (
                              <Button
                                variant="contained"
                                size="small"
                                color="error"
                                sx={{
                                  marginLeft: "20px",
                                }}
                                disableRipple
                                disableElevation
                                onClick={() => {
                                  setSelectedTicket(ticketRow);
                                  setIsAccept(false);
                                  setRejectReason("");
                                  setOpenRejectReasonDialog(true);
                                }}
                              >
                                受入拒否
                              </Button>
                            )}
                            <IconButton
                              id="fade-button"
                              aria-controls={openMenu ? "fade-menu" : undefined}
                              aria-haspopup="true"
                              aria-expanded={openMenu ? "true" : undefined}
                              onClick={(
                                event: React.MouseEvent<HTMLElement>
                              ) => {
                                setAnchorEl(event.currentTarget);
                                setSelectedTicket(ticketRow);
                              }}
                              sx={{
                                marginLeft: "20px",
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <DownloadImageDialog
        open={openDownloadDialog}
        onClose={() => {
          handleCloseDialog();
          if (isRefresh) {
            props.fetchData();
            setIsRefresh(false);
          }
        }}
        imageUrl={imageData || ""}
        imageType={imageName?.split(".").pop() || "png"}
        imageName={imageName || ""}
        onDownload={handleDownloadFromDialog}
        loading={loading}
      />
      <ConfirmDialog
        dialogType={
          isAccept === true
            ? ConfirmDialogType.customerAcceptEmployee
            : ConfirmDialogType.customerRejectEmployee
        }
        open={openAcceptOrRejectDialog}
        onClose={() => {
          setOpenAcceptOrRejectDialog(false);
        }}
        onConfirm={async () => {
          if (selectedTicket) {
            await axiosConfig.patch(`/dashboard/tickets/${selectedTicket.id}`, {
              status: isAccept
                ? TicketStatus.indexOf("CUSTOMER_INSPECTION_COMPLETED")
                : TicketStatus.indexOf("CUSTOMER_DELIVERY_REJECTED"),
            });
            props.fetchData();
          }
          setOpenAcceptOrRejectDialog(false);
        }}
        estimationNumber={row.order.estimation_number}
        fileName={selectedTicket?.file_name || ""}
      />
      {/* 差し戻し理由入力モーダル（設計05: 受入拒否は理由必須） */}
      <Dialog
        open={openRejectReasonDialog}
        onClose={() => {
          if (!rejectSubmitting) setOpenRejectReasonDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>受入拒否の理由</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            注文「{row.order.estimation_number}」のファイル「
            {selectedTicket?.file_name || ""}
            」を差し戻します。理由の入力は必須です。
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder="差し戻しの理由を入力してください"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRejectReasonDialog(false)}
            disabled={rejectSubmitting}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason.trim() || rejectSubmitting}
            onClick={async () => {
              if (!selectedTicket) return;
              setRejectSubmitting(true);
              try {
                await axiosConfig.patch(
                  `/dashboard/tickets/${selectedTicket.id}`,
                  {
                    status: TicketStatus.indexOf("CUSTOMER_DELIVERY_REJECTED"),
                  }
                );
                // 理由をコメントとして public でタイムラインに自動投稿
                await createComment({
                  order_id: row.order.id,
                  ticket_id: selectedTicket.id,
                  body: rejectReason,
                  kind: "rejection_reason",
                  visibility: "public",
                });
                setOpenRejectReasonDialog(false);
                setRejectReason("");
                props.fetchData();
                dispatch(
                  showSnackbar({
                    message: "差し戻しを確定しました",
                    type: "success",
                  })
                );
              } catch (e) {
                dispatch(
                  showSnackbar({
                    message: "差し戻しに失敗しました",
                    type: "error",
                  })
                );
              } finally {
                setRejectSubmitting(false);
              }
            }}
          >
            拒否を確定
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        id="fade-menu"
        MenuListProps={{
          "aria-labelledby": "fade-button",
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={() => {
          setAnchorEl(null);
        }}
        TransitionComponent={Fade}
      >
        <MenuItem
          onClick={() => {
            handleDownloadInitialFileClick();
            setImageName(selectedTicket!.file_name);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <SaveAlt
              sx={{
                color: "#4CAF50",
              }}
            />
          </ListItemIcon>
          <Typography
            variant="inherit"
            sx={{
              color: "#4CAF50",
            }}
          >
            ファイル
          </Typography>
        </MenuItem>
        <MenuItem
          disabled={
            TicketStatus.indexOf("COMPANY_DELIVERED") !==
              selectedTicket?.status &&
            TicketStatus.indexOf("CUSTOMER_DELIVERY_REJECTED") !==
              selectedTicket?.status &&
            TicketStatus.indexOf("CUSTOMER_INSPECTION_ONGOING") !==
              selectedTicket?.status &&
            TicketStatus.indexOf("CUSTOMER_INSPECTION_COMPLETED") !==
              selectedTicket?.status
          }
          onClick={() => {
            handleDownloadResultFileClick();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <SaveAlt
              sx={{
                color: "#4CAF50",
              }}
            />
          </ListItemIcon>
          <Typography
            variant="inherit"
            sx={{
              color: "#4CAF50",
            }}
          >
            納品物
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTicketId(selectedTicket?.id);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <InfoOutlined
              sx={{
                color: "#3964B1",
              }}
            />
          </ListItemIcon>
          <Typography
            variant="inherit"
            sx={{
              color: "#3964B1",
            }}
          >
            詳細
          </Typography>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}

const CustomerDashboard: React.FC = () => {
  const [showSearchContent, setShowSearchContent] = useState(true);
  const [openLoadingDialog, setOpenLoadingDialog] = useState(false);
  const [data, setData] = useState<PaginationResponse<DataOrder>>({
    data: [],
    metadata: {
      page: 1,
      per_page: 10,
      page_count: 0,
      total_count: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const [ticketId, setTicketId] = useState<string>();
  const [orderTicketId, setOrderTicketId] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isErrorEstOpen, setIsErrorEstOpen] = useState(false);

  const searchQuery = useRef({
    project_name: "",
    file_name: "",
    estimation_number: "",
    status: "-1",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const url = "/dashboard/orders";

    let queryString = "";
    for (const key in searchQuery.current) {
      if (searchQuery.current[key as keyof typeof searchQuery.current]) {
        queryString += queryString !== "" ? "&" : "?";
        queryString += `${key}=${
          searchQuery.current[key as keyof typeof searchQuery.current]
        }`;
      }
    }

    const response = await axiosConfig.get(`${url}${queryString}`);
    setLoading(false);
    setData(response.data.body);
  }, [searchParams]);

  const handleSearch = () => {
    setSearchParams((params) => {
      for (const key in searchQuery.current) {
        if (searchQuery.current[key as keyof typeof searchQuery.current]) {
          params.set(
            key,
            String(searchQuery.current[key as keyof typeof searchQuery.current])
          );
        } else {
          params.delete(key);
        }
      }
      return params;
    });
  };

  const StyledDialog = styled(Dialog)`
    text-align: center;
  `;

  const autocompleteOptions = useMemo(() => {
    // ラベル → 内部ステータス番号（カンマ区切り）へ変換した選択肢を生成する。
    // 「すべて」= -1、「見積不可」= -3（擬似ステータス。FailedEstimateSection で表示）。
    return [
      { key: "-1", value: "すべて" },
      { key: "-3", value: "見積不可" },
      ...customerStatusFilterOptions.map((opt) => ({
        key: opt.statusKeys
          .map((k) => TicketStatus.indexOf(k))
          .join(","),
        value: opt.label,
      })),
    ];
  }, []);

  const [status, setStatus] = useState("-1");

  const navigate = useNavigate();

  useEffect(() => {
    searchQuery.current = {
      project_name: searchParams.get("project_name") || "",
      file_name: searchParams.get("file_name") || "",
      estimation_number: searchParams.get("estimation_number") || "",
      status:
        searchParams.get("status") === "-1"
          ? ""
          : searchParams.get("status") || "",
    };
    setStatus(searchQuery.current.status ?? "-1");

    fetchData();
  }, [searchParams, fetchData]);

  return (
    <div className=" flex flex-col">
      <Header pageTitle="顧客ダッシュボード"></Header>
      <Grid container sx={{ height: "90vh" }} className="pb-6">
        <Grid item xs={1.5} className="py-2 bg-gray-100 h-full">
          <List component="nav" sx={{ color: "#3E6EB4" }}>
            <ListItem
              sx={{
                pb: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  margin: "auto",
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "#3E6EB4",
                  },
                }}
                onClick={() => {
                  navigate("/");
                }}
              >
                <ListItemText primary={"見積もりを作成"} />
              </Button>
            </ListItem>

            <ListItemButton
              selected={true}
              onClick={(event) => {
                handleSearch();
                setTicketId(undefined);
              }}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#3E6EB4",
                  color: "#fff",
                },
              }}
            >
              <ListAlt className="mr-4" />
              <ListItemText primary={"案件一覧"} />
            </ListItemButton>
          </List>
        </Grid>
        <Grid
          item
          xs={10.5}
          className="h-full pb-6 pt-4 px-4"
          sx={{ overflowX: "hidden", overflowY: "scroll" }}
        >
          <Card
            className="mx-auto w-full"
            sx={{ border: 1, borderColor: "grey.300" }}
          >
            <CardHeader
              sx={{ bgcolor: "#F5F5F5", paddingY: "5px" }}
              subheader={
                <Button
                  variant="text"
                  sx={{ padding: 0, color: "black" }}
                  disableRipple={true}
                  onClick={() => setShowSearchContent(!showSearchContent)}
                >
                  絞り込み
                </Button>
              }
            />
            {showSearchContent && (
              <CardContent sx={{ paddingY: "5px" }}>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <h3>案件名</h3>
                    <Autocomplete
                      freeSolo
                      className="mt-1"
                      size="small"
                      disableClearable
                      renderInput={(params) => <TextField {...params} />}
                      options={[
                        ...new Set(
                          data.data.map((row) => row.order.project_name)
                        ),
                      ]}
                      value={searchQuery.current.project_name}
                      onInputChange={(event, value) =>
                        (searchQuery.current.project_name = value)
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <h3>ファイル名</h3>
                    <Autocomplete
                      freeSolo
                      className="mt-1"
                      size="small"
                      disableClearable
                      renderInput={(params) => <TextField {...params} />}
                      options={[
                        ...new Set(
                          data.data
                            .map((row) =>
                              row.tickets.map((ticket) => ticket.file_name)
                            )
                            .flat()
                        ),
                      ]}
                      getOptionLabel={(option) => option || ""}
                      value={searchQuery.current.file_name}
                      onInputChange={(event, value) =>
                        (searchQuery.current.file_name = value)
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <h3>ステータス</h3>
                    <Autocomplete
                      className="mt-1"
                      size="small"
                      onKeyDown={(e) => e.preventDefault()}
                      disableClearable
                      options={autocompleteOptions}
                      getOptionLabel={(option) => option.value ?? ""}
                      value={{
                        key: status,
                        value:
                          autocompleteOptions.find((o) => o.key === status)
                            ?.value ?? "すべて",
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.key === value.key
                      }
                      renderInput={(params) => <TextField {...params} />}
                      onChange={(_, value) => {
                        searchQuery.current.status = value?.key ?? "-1";
                        setStatus(value?.key ?? "-1");
                      }}
                    />
                  </Grid>
                </Grid>
                <Stack className="mt-1">
                  <div className="ml-auto">
                    <Button
                      variant="contained"
                      sx={{ paddingX: "30px" }}
                      onClick={() => handleSearch()}
                    >
                      検索
                    </Button>
                  </div>
                </Stack>
              </CardContent>
            )}
          </Card>
          <FailedEstimateSection
            visible={status === "" || status === "-1" || status === "-3"}
          />
          {status !== "-3" && (
          <Card
            className="mx-auto w-full mt-4"
            sx={{ width: "100%", border: 1, borderColor: "grey.300" }}
          >
            <CardHeader
              sx={{ bgcolor: "#F5F5F5", paddingY: "10px" }}
              subheader={"案件リスト"}
            ></CardHeader>
            <CardContent>
              <TableContainer component={Paper}>
                <Table aria-label="collapsible table" size="small">
                  {loading && (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        paddingY: 10,
                      }}
                    >
                      <CircularProgress
                        sx={{ marginLeft: "auto", marginRight: "auto" }}
                      />
                    </Box>
                  )}
                  <TableBody>
                    {!loading &&
                      data?.data.map((row: DataOrder) => (
                        <Row
                          key={row.order.id}
                          row={row}
                          setTicketId={setTicketId}
                          fetchData={fetchData}
                          setOrderTicketId={setOrderTicketId}
                          setOpenLoadingDialog={setOpenLoadingDialog}
                          setIsErrorEstOpen={setIsErrorEstOpen}
                        />
                      ))}
                  </TableBody>
                  {!loading && data?.data.length <= 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        paddingY: 10,
                      }}
                    >
                      <p
                        style={{
                          marginLeft: "auto",
                          marginRight: "auto",
                        }}
                      >
                        データがありません。
                      </p>
                    </Box>
                  )}
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          )}
        </Grid>
      </Grid>
      <Footer></Footer>
      <LoadingDialog isOpen={!!openLoadingDialog} message={"処理中"} />
      <Dialog
        open={!!ticketId}
        onClose={() => setTicketId(undefined)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>チケット詳細</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setTicketId(undefined)}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent dividers>
          <TicketDetail ticketId={ticketId} />
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!orderTicketId}
        onClose={() => setOrderTicketId(undefined)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>注文詳細</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOrderTicketId(undefined)}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent dividers>
          <OrderDetails orderTicketId={orderTicketId} />
        </DialogContent>
      </Dialog>
      <StyledDialog open={isErrorEstOpen}>
        <DialogTitle id="alert-dialog-title">
          見積りの問題が発生しました
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            <Typography>詳細確認が必要なため、お見積りできませんでした。</Typography>
            <Typography>
              お手数ですがお見積フォームより別途お見積りをご依頼ください。
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ margin: "15px auto" }}
            onClick={() => setIsErrorEstOpen(false)}
            variant="outlined"
            disableElevation
          >
            戻る
          </Button>
          <Button
            sx={{ margin: "15px auto" }}
            href="https://cadtrace.jp/form/"
            variant="contained"
            disableElevation
            autoFocus
          >
            お見積りフォーム
          </Button>
        </DialogActions>
      </StyledDialog>
    </div>
  );
};

export default CustomerDashboard;
